import { MAX_LIST_CELL_LENGTH } from '@/entities/taxonomy';
import {
  withValueAndCode,
  type TaxonomyListColumn,
  type TaxonomyListRow,
} from '@/entities/taxonomy-list';
import { parseTabular } from './parseTabular';

// The server's limits (apps/assets-api/src/taxonomy/dto/taxonomy-list.dto.ts), checked here so a
// paste that would be refused says why before anything is created. The blank and repeated
// column-name checks are this paste's own rules; the server does not check names.
export const MAX_PASTED_LIST_COLUMNS = 50;
export const MAX_PASTED_LIST_ROWS = 20000;
export const MAX_LIST_COLUMN_NAME_LENGTH = 100;

/**
 * Quoted only when it has to be, so a copy reads back value for value; line breaks come back as
 * `\n` and column names are trimmed. Values are not escaped for spreadsheets: one starting with
 * `=` is evaluated as a formula if the text is pasted into one.
 */
function tsvCell(value: string): string {
  return /[\t\n\r"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * A list as tab-separated text: a header row of the stored column names, then one line per row.
 * Ids are left out on purpose: they belong to this environment, and the list is pasted into
 * another one. A blank name gets `blankName(position)` and a repeated one a ` (2)` suffix, since
 * {@link parsePastedList} refuses both and every copy must paste back.
 */
export function serializeList(
  columns: TaxonomyListColumn[],
  rows: TaxonomyListRow[],
  blankName: (position: number) => string,
): string {
  const lines = [headerNames(columns, blankName).map(tsvCell)];
  for (const row of rows) lines.push(columns.map((c) => tsvCell(row.cells[c.id] ?? '')));
  return lines.map((cells) => cells.join('\t')).join('\n');
}

/** At most `length` UTF-16 units, never splitting a surrogate pair. */
function shorten(text: string, length: number): string {
  let out = '';
  for (const char of text) {
    if (out.length + char.length > length) break;
    out += char;
  }
  return out.trimEnd();
}

function headerNames(
  columns: TaxonomyListColumn[],
  blankName: (position: number) => string,
): string[] {
  const seen = new Set<string>();
  return columns.map((column, index) => {
    const base = column.name.trim() || blankName(index + 1);
    let name = base;
    for (let n = 2; seen.has(name.toLowerCase()); n++) {
      const suffix = ` (${n})`;
      name = `${shorten(base, MAX_LIST_COLUMN_NAME_LENGTH - suffix.length)}${suffix}`;
    }
    seen.add(name.toLowerCase());
    return name;
  });
}

export type PastedListError =
  | { kind: 'empty' }
  | { kind: 'blankColumnName'; column: number }
  | { kind: 'duplicateColumnName'; name: string }
  | { kind: 'columnNameTooLong'; name: string }
  | { kind: 'tooManyColumns'; count: number }
  | { kind: 'tooManyRows'; count: number }
  | { kind: 'rowTooWide'; row: number }
  | { kind: 'cellTooLong'; row: number };

export type PastedList =
  | { ok: true; columns: TaxonomyListColumn[]; rows: TaxonomyListRow[] }
  | { ok: false; error: PastedListError };

/**
 * Reads text from {@link serializeList} (or any table with a header row) into new columns and rows.
 * Every id is new, as the server's own duplicate does, because ids from another environment mean
 * nothing here. Blank lines above the header and rows holding only spaces are skipped. A bad
 * row is numbered by its line in the paste, which holds unless a quoted cell above it spans lines.
 */
export function parsePastedList(
  text: string,
  newId: () => string = () => crypto.randomUUID(),
): PastedList {
  // Tabs only: a copied list always has two or more columns, and a one-column paste of
  // "Korea, Republic of" is one value. Inner blank rows are kept so row numbers stay in step.
  const parsed = parseTabular(text, { delimiter: '\t', keepBlankRows: true });
  const isBlank = (line: string[]) => line.every((cell) => cell.trim() === '');
  const skipped = parsed.findIndex((line) => !isBlank(line));
  if (skipped === -1) return { ok: false, error: { kind: 'empty' } };
  const [rawHeader, ...body] = parsed.slice(skipped);
  const rowNumber = (index: number) => skipped + index + 2;

  const header = [...rawHeader];
  while (header.length > 0 && header[header.length - 1].trim() === '') header.pop();
  if (header.length === 0) return { ok: false, error: { kind: 'empty' } };
  if (header.length > MAX_PASTED_LIST_COLUMNS) {
    return { ok: false, error: { kind: 'tooManyColumns', count: header.length } };
  }
  const seen = new Set<string>();
  for (const [index, raw] of header.entries()) {
    const name = raw.trim();
    if (!name) return { ok: false, error: { kind: 'blankColumnName', column: index + 1 } };
    if (name.length > MAX_LIST_COLUMN_NAME_LENGTH) {
      return { ok: false, error: { kind: 'columnNameTooLong', name } };
    }
    if (seen.has(name.toLowerCase())) {
      return { ok: false, error: { kind: 'duplicateColumnName', name } };
    }
    seen.add(name.toLowerCase());
  }
  const rowCount = body.filter((line) => !isBlank(line)).length;
  if (rowCount > MAX_PASTED_LIST_ROWS) {
    return { ok: false, error: { kind: 'tooManyRows', count: rowCount } };
  }

  const columns = header.map((name) => ({ id: newId(), name: name.trim() }));
  const rows: TaxonomyListRow[] = [];
  for (const [index, line] of body.entries()) {
    if (isBlank(line)) continue;
    const cells = [...line];
    while (cells.length > columns.length && cells[cells.length - 1] === '') cells.pop();
    if (cells.length > columns.length) {
      return { ok: false, error: { kind: 'rowTooWide', row: rowNumber(index) } };
    }
    const entries = cells.flatMap((value, col): [string, string][] =>
      value === '' ? [] : [[columns[col].id, value]],
    );
    if (entries.length === 0) continue;
    if (entries.some(([, value]) => value.length > MAX_LIST_CELL_LENGTH)) {
      return { ok: false, error: { kind: 'cellTooLong', row: rowNumber(index) } };
    }
    rows.push({ id: newId(), cells: Object.fromEntries(entries) });
  }

  return { ok: true, columns: withValueAndCode(columns, newId), rows };
}
